/** Dedicated single-purpose copy of StockCountSchedule's "Scheduled Stock Counts" tab
 * (Demo Mode nav restructure) - same data/loading logic as the original tabbed page's tab
 * one, but rendered as its own standalone screen instead of one of three tabs on a shared
 * page. The original tabbed page (index.tsx, route /stock-count-schedule/:tab) is untouched
 * and remains the production entry point. */

import { Box, Typography } from '@mui/material';
import ScheduleBox from './components/ScheduleBox';
import useNswagClient from '../../hooks/api/useNswagClient';
import { useContext, useEffect, useState } from 'react';
import { StockCheckScheduleDetail } from '../../app/services/api/generated';
import { UserContext } from '../../components/shared/useUser';
import { useTranslation } from 'react-i18next';
import PageTitle from '../../components/shared/PageTitle';
import PageArea from '../../components/shared/PageArea';
import useLogError from '../../hooks/useLogError';

const StockCounts = () => {
  const { getsStockCheckSchedule } = useNswagClient();
  const { t } = useTranslation('common');
  const { selectedStore } = useContext(UserContext);
  const storeID = selectedStore?.storeNumber ? selectedStore.storeNumber : 'No store selected';
  const [schedules, setSchedules] = useState<StockCheckScheduleDetail[]>([]);
  const [isSchedulesLoading, setIsSchedulesLoading] = useState(false);
  const [showAllStores, setShowAllStores] = useState(false);
  const { logError } = useLogError();

  useEffect(() => {
    loadData();
  }, [selectedStore]);

  const loadData = (allStores?: boolean) => {
    setIsSchedulesLoading(true);
    getsStockCheckSchedule(storeID, allStores)
      .then((result) => {
        if (result?.data) setSchedules(result.data);
      })
      .catch(logError)
      .finally(() => setIsSchedulesLoading(false));
  };

  return (
    <Box sx={{ width: '100%', textAlign: 'left' }}>
      <PageArea>
        <PageTitle>{t('countStockSchedule', 'Stock Counts')}</PageTitle>
        {selectedStore ? (
          <ScheduleBox schedules={schedules}
            storeID={storeID}
            loadData={loadData}
            isLoading={isSchedulesLoading}
            showAllStores={showAllStores}
            setShowAllStores={setShowAllStores} />
        ) : (
          <Box><Typography variant='textLG'>No store selected</Typography></Box>
        )}
      </PageArea>
    </Box>
  );
};

export default StockCounts;
